/**
 * 스크롤 스파이: 현재 스크롤 위치에서 보이는 섹션 id 계산
 * viewport 상단 근처(viewportTopOffset)를 지나는 섹션 중 가장 아래에 있는 것을 "현재 섹션"으로 선택
 */

const DEFAULT_VIEWPORT_TOP_OFFSET = 150;

export function computeCurrentSectionId(
  sectionIds: string[],
  getScrollTop: () => number,
  getSectionTop: (id: string) => number | null,
  viewportTopOffset: number = DEFAULT_VIEWPORT_TOP_OFFSET,
): string | null {
  if (sectionIds.length === 0) return null;
  const scrollTop = getScrollTop();
  const viewportTop = scrollTop + viewportTopOffset;
  let currentId: string | null = null;
  for (let i = 0; i < sectionIds.length; i++) {
    const top = getSectionTop(sectionIds[i]);
    if (top !== null && top <= viewportTop) {
      currentId = sectionIds[i];
    }
  }
  return currentId ?? sectionIds[0];
}

/**
 * DOM 순서와 sectionIds 순서가 같은 화면 전용 최적화 버전.
 * 모든 section의 레이아웃을 읽지 않고 이진 탐색해, 긴 문서에서도 한 프레임당
 * getSectionTop 호출을 O(log n)으로 제한한다.
 *
 * 중간 section DOM이 누락되면 순서 가정을 사용할 수 없으므로 기존 선형 계산으로
 * 폴백한다. 비교 화면은 모든 chunk placeholder가 처음부터 있어 정상적으로는
 * 폴백하지 않는다.
 */
export function computeCurrentOrderedSectionId(
  sectionIds: string[],
  getScrollTop: () => number,
  getSectionTop: (id: string) => number | null,
  viewportTopOffset: number = DEFAULT_VIEWPORT_TOP_OFFSET,
): string | null {
  if (sectionIds.length === 0) return null;

  const viewportTop = getScrollTop() + viewportTopOffset;
  let low = 0;
  let high = sectionIds.length - 1;
  let currentIndex = 0;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const top = getSectionTop(sectionIds[middle]);
    if (top === null) {
      return computeCurrentSectionId(
        sectionIds,
        getScrollTop,
        getSectionTop,
        viewportTopOffset,
      );
    }

    if (top <= viewportTop) {
      currentIndex = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return sectionIds[currentIndex];
}
